import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import Loading from 'components/commons/loading/Loading';
import { useTargetAchievementSupportContext } from 'contexts/TargetAchievementSupportContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useModal } from 'hooks/useModal';
import { useCustomHeaderTitle } from 'hooks/useCustomHeaderTitle';
import { useScroll } from 'hooks/useScroll';
import { updateTarget } from 'config/api';
import { formatNumber } from 'utils/formatter';
import { Mode } from 'components/features/target-achievement-support/pages/TargetUpdateStep1';
import {
  TargetAccountItem,
  GlobalTargetProps,
  Target,
} from 'components/features/target-achievement-support/utils/type';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import { dateToString } from 'components/features/target-achievement-support/utils/utils';
import { useBirthday } from 'components/features/target-achievement-support/utils/birthday';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { CheckYieldCalculationForm } from '../CheckYieldCalculationForm';
import { Page, StepArea } from '../StepArea';
import { ExitSetHowToSaveModal } from '../modal/ExitSetHowToSaveModal';
import { SavingsCompleteModal } from '../modal/SavingsCompleteModal';
import { TargetBasicInfoForm } from '../../share/TargetBasicInfoForm';
import { NotFoundBirthday } from '../../birthday/NotFoundBirthday';
import { TargetPremium } from '../../TargetPremium';

export const getTotalAccountsAmount = (target: Target) => {
  const validAcounts = target.accountList.filter(
    list => list.available === true
  );
  const totalAssetAmount = validAcounts.reduce(
    (acc, v) => acc + v.totalAssetAmount,
    0
  );
  return totalAssetAmount;
};

export const AssetAmountInfoForm = ({
  selectedAccounts,
  excludeAmount,
  update,
}: {
  selectedAccounts: TargetAccountItem[];
  excludeAmount: number;
  update: () => void;
}) => {
  const totalAmount_ = selectedAccounts
    .map(item => (item.available ? item.totalAssetAmount : 0))
    .reduce((acc, v) => acc + v, 0);
  const totalAmount = totalAmount_ - excludeAmount;

  return (
    <div className="mx-2 mt-8 p-4 text-bluegray-700 bg-white rounded-lg shadow">
      <div className="mx-1">
        <div className="flex flex-row items-center">
          <div className="font-bold">初期投資金額</div>
          <div
            className="flex flex-row ml-auto text-xs text-blue-700 cursor-pointer"
            onClick={() => update()}>
            <div>変更</div>
            <div className="icon-edit text-sm ml-2" />
          </div>
        </div>
        <div className="mt-2 ml-4 mr-2">
          {selectedAccounts.length === 0 ? (
            <div className="text-sm">連携口座なし</div>
          ) : (
            <>
              {selectedAccounts.map((item, i) => {
                return (
                  <React.Fragment key={i}>
                    <div className="flex flex-row mt-1 text-sm">
                      <div>{item.brokerName}</div>
                      <div className="ml-auto">
                        {item.available ? (
                          <>
                            {formatNumber(item.totalAssetAmount, {})}
                            <span className="text-xs ml-1">円</span>
                          </>
                        ) : (
                          <>集計対象外</>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
        <div className="flex flex-row ml-4 mr-2 mt-4 text-sm">
          <div className="font-bold">うち除外金額</div>
          <div className="ml-auto">
            <span className="mr-1">{formatNumber(excludeAmount, {})}</span>円
          </div>
        </div>
      </div>
      <div className="my-2 border border-bluegrayslate-200" />
      <div className="flex flex-row items-center p-4 text-sm font-bold bg-bluegray-50 rounded">
        <div>合計</div>
        <div className="ml-auto">
          <span className="text-base mr-1">
            {formatNumber(totalAmount, {})}
          </span>
          円
        </div>
      </div>
    </div>
  );
};

const TargetUpdateConfirmInternal = ({
  id,
  mode,
  updateGlobalTarget,
  confirmGlobalTarget,
  updateConfirmGlobalTarget,
}: {
  id: string;
  mode: Mode;
  updateGlobalTarget: (data: Target | null) => void;
  confirmGlobalTarget: Target;
  updateConfirmGlobalTarget: (data: Target | null) => void;
}) => {
  const {
    goTargetUpdateInfo,
    goTargetPushUpdateStep1,
    goTargetPushUpdateStep2,
    goTargetReplaceUpdateStep2,
    goTargetPushDetail,
  } = useTargetSupportRouting();

  const totalAccountsAmount = getTotalAccountsAmount(confirmGlobalTarget);
  const selectedAccounts: TargetAccountItem[] =
    confirmGlobalTarget.targetAmount > totalAccountsAmount
      ? confirmGlobalTarget.accountList
      : [];

  const excludeAmount =
    confirmGlobalTarget.targetAmount > totalAccountsAmount
      ? confirmGlobalTarget.excludeAmount
      : 0;

  const [
    isShowExitModal,
    { hideModal: hideExitModal, showModal: showExitModal },
  ] = useModal();

  const [
    isShowSavingsCompleteModal,
    {
      hideModal: hideSavingsCompleteModal,
      showModal: showSavingsCompleteModal,
    },
  ] = useModal();

  const { setBack } = useCustomizeHeaderContext();
  useEffect(() => {
    setBack({ backFunction: showExitModal });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTarget = () => {
    if (confirmGlobalTarget.targetAmount < totalAccountsAmount) {
      const updateTarget = {
        ...confirmGlobalTarget,
        accountList: selectedAccounts,
        excludeAmount,
      };
      updateGlobalTarget(updateTarget);
      updateConfirmGlobalTarget(updateTarget);
    }
  };

  const goUpdateInfo = async (): Promise<void> => {
    await updateTarget();
    goTargetUpdateInfo(id, mode);
  };

  const goStep1 = async (): Promise<void> => {
    await updateTarget();
    goTargetPushUpdateStep1(id, mode);
  };

  const goStep2 = async (): Promise<void> => {
    await updateTarget();
    goTargetPushUpdateStep2(id, mode);
  };

  const register = (): void => {
    showSavingsCompleteModal();
    return;
  };

  const submit = async () => {
    const accountIDList = selectedAccounts.map(list => list.account);
    const updTargetData = {
      ...confirmGlobalTarget,
      accountIDList,
      excludeAmount,
      startDate: new Date(confirmGlobalTarget.startDate),
      endDate: new Date(confirmGlobalTarget.endDate),
    };

    const ret = await updateTargetAPI(id, updTargetData);
    return ret;
  };

  useScroll();

  return (
    <>
      <div className="mt-6">
        {mode === 'new' && <StepArea {...{ currentPage: 'confirm' as Page }} />}
        <TargetBasicInfoForm
          {...{ target: confirmGlobalTarget, update: () => goUpdateInfo() }}
        />
        <AssetAmountInfoForm
          {...{
            selectedAccounts,
            excludeAmount,
            update: () => goStep1(),
          }}
        />

        <div className="mx-2 mt-8 p-4 text-bluegray-700 bg-white rounded-lg shadow">
          <div className="flex flex-row items-center mx-1">
            <div className="font-bold">毎月のつみたて額</div>
            <div
              className="flex flex-row ml-auto text-xs text-blue-700 cursor-pointer"
              onClick={() => goStep2()}>
              <div>変更</div>
              <div className="icon-edit text-sm ml-2" />
            </div>
          </div>
          <div className="my-2 border border-bluegrayslate-200" />
          <div className="flex flex-row items-center p-4 text-sm font-bold bg-bluegray-50 rounded">
            <div>金額</div>
            <div className="ml-auto">
              <span className="text-base mr-1">
                {formatNumber(confirmGlobalTarget.reserveAmount, {})}
              </span>
              円/月
            </div>
          </div>
          <CheckYieldCalculationForm
            {...{
              id,
              mode,
              target: confirmGlobalTarget,
              selectedAccounts,
              excludeAmount,
            }}
          />
        </div>

        <div
          className="p-2 mt-6 mx-2 font-bold text-base text-white text-center bg-tradewell rounded-lg shadow cursor-pointer"
          onClick={register}>
          この貯め方で目標をスタートする
        </div>
        <div
          className="p-2 mt-4 mx-2 font-bold text-base text-bluegray-700 text-center bg-white border border-bluegray-400 rounded-lg shadow cursor-pointer"
          onClick={showExitModal}>
          戻る
        </div>
      </div>

      <ExitSetHowToSaveModal
        {...{
          hide: hideExitModal,
          isShow: isShowExitModal,
          onQuit: () => {
            updateConfirmGlobalTarget(null);
            goTargetReplaceUpdateStep2(id, mode);
          },
          onContinueInput: hideExitModal,
        }}
      />
      <SavingsCompleteModal
        {...{
          hide: hideSavingsCompleteModal,
          isShow: isShowSavingsCompleteModal,
          submit: submit,
          finish: () => goTargetPushDetail(id),
          updateGlobalTarget,
          updateConfirmGlobalTarget,
        }}
      />
    </>
  );
};

export const TargetUpdateConfirmNew = ({
  id,
  mode, // new
}: {
  id: string;
  mode: Mode;
}) => {
  useCustomHeaderTitle('確認画面');
  const { birthday } = useBirthday();
  const {
    globalTarget,
    updateGlobalTarget,
    confirmGlobalTarget,
    updateConfirmGlobalTarget,
  } = useTargetAchievementSupportContext();

  if (globalTarget !== null && confirmGlobalTarget === null) {
    updateConfirmGlobalTarget(globalTarget);
  }

  if (globalTarget === null && confirmGlobalTarget === null) {
    return <Redirect to={`/target/${id}`} />;
  }

  if (
    globalTarget === null ||
    confirmGlobalTarget === null ||
    id !== globalTarget.id
  ) {
    return <Loading loading style={{ marginTop: '3rem' }} />;
  }

  if (birthday === null || birthday === undefined) {
    return (
      <PremiumCheck content={<TargetPremium />}>
        <NotFoundBirthday />
      </PremiumCheck>
    );
  }

  return (
    <TargetUpdateConfirmInternal
      {...{
        id,
        mode,
        updateGlobalTarget,
        confirmGlobalTarget,
        updateConfirmGlobalTarget,
      }}
    />
  );
};

const updateTargetAPI = async (
  id: string,
  target: GlobalTargetProps
): Promise<{ tag: 'Success' | 'Error' }> => {
  const ret = await updateTarget({
    ID: id,
    TargetAmount: target.targetAmount,
    EndDate: dateToString(target.endDate),
    StartDate: dateToString(new Date()),
    Title: target.title,
    RefType: target.refType,
    ...(target.excludeAmount !== null &&
    target.reserveAmount !== null &&
    target.accountIDList !== null
      ? {
          AccountIDList: target.accountIDList,
          ExcludeAmount: target.excludeAmount,
          ReserveAmount: target.reserveAmount,
        }
      : {}),
    ...(target.managementType
      ? { ManagementType: target.managementType }
      : null),
  });

  if (ret.ResultCode === 0) {
    return { tag: 'Success' };
  } else {
    return { tag: 'Error' };
  }
};
